/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { StyledEngineProvider } from "@mui/material/styles";
import ScrollToTop from "./services/ScrollToTop";
import CookieBanner from "./components/CookieBanner/CookieBanner";
import CssGlobal from "./components/CssGlobal/CssGlobal";
import Landingpage from "./routes/LandingPage/Landingpage";
import Base from "./routes/Base";
import Jobs from "./routes/Jobs/Jobs";
import Serviceworld from "./routes/Serviceworld/Serviceworld";
import Checkout from "./routes/Checkout/Checkout";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import { LocalizationProvider } from "@mui/x-date-pickers";
import Guidebook from "./routes/Guidebook/Guidebook";
import RequireAuth from "./components/RequireAuth/RequireAuth";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { CustomerProvider } from "./provider/Customer/CustomerProvider";
import { HelmetProvider } from "react-helmet-async";
import "react-toastify/dist/ReactToastify.css";
import StyledContainer from "./components/StyledToastContainer/StyledToastContainer";
import ThemeProviderWrapper from "./components/ThemeProviderWrapper/ThemeProviderWrapper";
import IconsOverviewPage from "./containers/Pages/Generic/IconsOverviewPage/IconsOverviewPage";
import ThemeOverviewPage from "./containers/Pages/Generic/ThemeOverviewPage/ThemeOverviewPage";
import { GoogleMapsApiProvider } from "./provider/GoogleMapsApi/GoogleMapsApiProvider";
import { CookieConsentProvider } from "./provider/CookieConsent/CookieConsentProvider";
import LibInitializer from "./components/LibInitializer/LibInitializer";
import ClientVersionChecker from "./components/ClientVersionChecker/ClientVersionChecker";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { ScrollToHash } from "./components/ScrollToHash/ScrollToHash";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      staleTime: 5 * 1000, //in ms
    },
  },
});

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.locale("de");

function App() {
  return (
    <CookieConsentProvider>
      <LibInitializer>
        <QueryClientProvider client={queryClient}>
          <StyledEngineProvider injectFirst>
            <CustomerProvider>
              <ThemeProviderWrapper>
                <CssGlobal />
                <BrowserRouter>
                  <ClientVersionChecker>
                    {/* TODO: Use ScrollRestoration after react router update */}
                    <ScrollToHash />
                    <HelmetProvider>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={"de"}
                        localeText={{
                          cancelButtonLabel: "Abbrechen",
                          okButtonLabel: "OK",
                          fieldYearPlaceholder: () => "jjjj",
                          fieldMonthPlaceholder: () => "mm",
                          fieldDayPlaceholder: () => "tt",
                          fieldHoursPlaceholder: () => "hh",
                          fieldMinutesPlaceholder: () => "mm",
                          fieldSecondsPlaceholder: () => "ss",
                        }}
                      >
                        <GoogleMapsApiProvider>
                          <ScrollToTop>
                            <Routes>
                              <Route
                                path={"/checkout/*"}
                                element={
                                  <RequireAuth>
                                    <Checkout />
                                  </RequireAuth>
                                }
                              />
                              <Route
                                path={"/servicewelt/*"}
                                element={
                                  <RequireAuth>
                                    <Serviceworld />
                                  </RequireAuth>
                                }
                              />
                              <Route path={"/jobs/*"} element={<Jobs />} />
                              <Route path={"/ratgeber/*"} element={<Guidebook />} />
                              <Route path={"/lp/*"} element={<Landingpage />} />
                              <Route path={"/uebersicht-icons"} element={<IconsOverviewPage />} />
                              <Route path={"/theme"} element={<ThemeOverviewPage />} />
                              <Route path={"/*"} element={<Base />} />
                            </Routes>
                            <CookieBanner />
                            <StyledContainer position="top-center" autoClose={10000} closeOnClick pauseOnFocusLoss draggable />
                          </ScrollToTop>
                        </GoogleMapsApiProvider>
                      </LocalizationProvider>
                    </HelmetProvider>
                  </ClientVersionChecker>
                </BrowserRouter>
              </ThemeProviderWrapper>
            </CustomerProvider>
          </StyledEngineProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </LibInitializer>
    </CookieConsentProvider>
  );
}

export default App;
